// Company profile form (ADR-0047): organization type, legal and tax data, commercial contact, fiscal
// address and billing contact (CA-O3: any country; invoices are issued from Honduras). Used by the first
// registration (/onboarding) and by the workspace settings. The server validates the tax id format per
// country and decides the review status; the form only collects and explains.
import { useState, type SyntheticEvent } from "react";
import { ApiProblem } from "@/api/errors";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { COUNTRIES, ruleMessage, TAX_LABEL } from "./labels";

export interface CompanyValues {
  orgType: "AGENCY" | "DIRECT_CLIENT";
  legalName: string;
  country: string;
  taxId: string;
  website: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  region: string;
  postalCode: string | null;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
}

export const EMPTY_COMPANY: CompanyValues = {
  orgType: "AGENCY",
  legalName: "",
  country: "HN",
  taxId: "",
  website: null,
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  addressLine1: "",
  addressLine2: null,
  city: "",
  region: "",
  postalCode: null,
  billingContactName: "",
  billingContactEmail: "",
  billingContactPhone: "",
};

/** Form values from a stored profile (a profile registered before CA-O3 has no address yet). */
export function companyValuesOf(p: Partial<Record<keyof CompanyValues, unknown>>): CompanyValues {
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const opt = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);
  return {
    orgType: p.orgType === "DIRECT_CLIENT" ? "DIRECT_CLIENT" : "AGENCY",
    legalName: str(p.legalName),
    country: str(p.country) || "HN",
    taxId: str(p.taxId),
    website: opt(p.website),
    contactName: str(p.contactName),
    contactEmail: str(p.contactEmail),
    contactPhone: str(p.contactPhone),
    addressLine1: str(p.addressLine1),
    addressLine2: opt(p.addressLine2),
    city: str(p.city),
    region: str(p.region),
    postalCode: opt(p.postalCode),
    billingContactName: str(p.billingContactName),
    billingContactEmail: str(p.billingContactEmail),
    billingContactPhone: str(p.billingContactPhone),
  };
}

const select =
  "h-8 w-full rounded-md border border-line-strong bg-surface px-2 text-sm text-fg focus-visible:outline-2 focus-visible:outline-brand";

export function CompanyForm({
  initial,
  submitLabel,
  legalLocked = false,
  onSubmit,
}: {
  initial: CompanyValues;
  submitLabel: string;
  /** A validated company changes legal data only through Media Interactiva. */
  legalLocked?: boolean;
  onSubmit: (values: CompanyValues) => Promise<void>;
}) {
  const [v, setV] = useState<CompanyValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const set = <K extends keyof CompanyValues>(key: K, value: CompanyValues[K]) => {
    setV((prev) => ({ ...prev, [key]: value }));
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setPending(true);
    setErrors({});
    setFormError(undefined);
    try {
      const opt = (x: string | null) => (x?.trim() ? x.trim() : null);
      await onSubmit({
        ...v,
        website: opt(v.website),
        addressLine2: opt(v.addressLine2),
        postalCode: opt(v.postalCode),
      });
    } catch (problem) {
      if (problem instanceof ApiProblem) {
        const fields = problem.fieldErrors();
        if (problem.fields.some((f) => f.problem === "INVALID_FORMAT"))
          fields.taxId = `Revisa el formato del ${TAX_LABEL[v.country] ?? "número"} para el país elegido.`;
        setErrors(fields);
        if (Object.keys(fields).length === 0 || problem.code === "COMMERCIAL_RULE") setFormError(ruleMessage(problem));
      } else setFormError("No pudimos guardar los datos. Inténtalo de nuevo.");
    } finally {
      setPending(false);
    }
  };
  const taxLabel = TAX_LABEL[v.country] ?? "Identificación tributaria";
  return (
    <form className="grid gap-5" onSubmit={(e) => void submit(e)} noValidate>
      <fieldset className="grid gap-2" disabled={legalLocked}>
        <legend className="mb-1 text-xs font-medium text-fg">Tipo de organización</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {(
            [
              ["AGENCY", "Agencia", "Gestiono campañas de varios anunciantes."],
              ["DIRECT_CLIENT", "Cliente directo", "Anuncio mi propia empresa o marca."],
            ] as const
          ).map(([value, label, hint]) => (
            <label
              key={value}
              className="flex cursor-pointer gap-2 rounded-md border border-line p-3 text-sm has-[:checked]:border-brand has-[:checked]:bg-brand-soft"
            >
              <input
                type="radio"
                name="orgType"
                value={value}
                checked={v.orgType === value}
                onChange={() => {
                  set("orgType", value);
                }}
              />
              <span className="grid gap-0.5">
                <span className="font-medium">{label}</span>
                <span className="text-xs text-muted">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2" disabled={legalLocked}>
        <legend className="mb-1 text-xs font-medium text-fg sm:col-span-2">Datos de la empresa</legend>
        <Field label="Nombre legal de la empresa" error={errors.legalName} className="sm:col-span-2">
          <Input
            value={v.legalName}
            onChange={(e) => {
              set("legalName", e.target.value);
            }}
            maxLength={200}
            required
            autoComplete="organization"
          />
        </Field>
        <Field label="País" error={errors.country}>
          <select
            className={select}
            value={v.country}
            onChange={(e) => {
              set("country", e.target.value);
            }}
          >
            {COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={taxLabel}
          error={errors.taxId}
          hint={TAX_LABEL[v.country] ? undefined : "Validamos solo el formato básico para este país."}
        >
          <Input
            value={v.taxId}
            onChange={(e) => {
              set("taxId", e.target.value);
            }}
            maxLength={40}
            required
            inputMode="text"
          />
        </Field>
      </fieldset>
      {legalLocked ? (
        <p className="text-xs text-muted">
          Tu empresa ya está validada: para corregir sus datos legales, escribe a Media Interactiva.
        </p>
      ) : null}
      <Field label="Sitio web" hint="Opcional." error={errors.website}>
        <Input
          value={v.website ?? ""}
          onChange={(e) => {
            set("website", e.target.value);
          }}
          placeholder="https://"
          type="url"
          maxLength={300}
          autoComplete="url"
        />
      </Field>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-medium text-fg sm:col-span-2">Contacto comercial</legend>
        <Field label="Nombre completo" error={errors.contactName} className="sm:col-span-2">
          <Input
            value={v.contactName}
            onChange={(e) => {
              set("contactName", e.target.value);
            }}
            maxLength={120}
            required
            autoComplete="name"
          />
        </Field>
        <Field
          label="Correo electrónico"
          hint="Puede ser distinto del correo con el que inicias sesión."
          error={errors.contactEmail}
        >
          <Input
            value={v.contactEmail}
            onChange={(e) => {
              set("contactEmail", e.target.value);
            }}
            type="email"
            maxLength={320}
            required
            autoComplete="email"
          />
        </Field>
        <Field label="Teléfono" error={errors.contactPhone}>
          <Input
            value={v.contactPhone}
            onChange={(e) => {
              set("contactPhone", e.target.value);
            }}
            type="tel"
            maxLength={24}
            required
            autoComplete="tel"
          />
        </Field>
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-medium text-fg sm:col-span-2">Dirección fiscal</legend>
        <p className="-mt-1 text-xs text-muted sm:col-span-2">
          La dirección de la empresa en el país elegido. Media Interactiva factura desde Honduras a esta dirección.
        </p>
        <Field label="Dirección" error={errors.addressLine1} className="sm:col-span-2">
          <Input
            value={v.addressLine1}
            onChange={(e) => {
              set("addressLine1", e.target.value);
            }}
            maxLength={200}
            required
            autoComplete="address-line1"
          />
        </Field>
        <Field
          label="Complemento"
          hint="Opcional: oficina, piso, referencia."
          error={errors.addressLine2}
          className="sm:col-span-2"
        >
          <Input
            value={v.addressLine2 ?? ""}
            onChange={(e) => {
              set("addressLine2", e.target.value);
            }}
            maxLength={200}
            autoComplete="address-line2"
          />
        </Field>
        <Field label="Ciudad" error={errors.city}>
          <Input
            value={v.city}
            onChange={(e) => {
              set("city", e.target.value);
            }}
            maxLength={120}
            required
            autoComplete="address-level2"
          />
        </Field>
        <Field label="Departamento, estado o provincia" error={errors.region}>
          <Input
            value={v.region}
            onChange={(e) => {
              set("region", e.target.value);
            }}
            maxLength={120}
            required
            autoComplete="address-level1"
          />
        </Field>
        <Field label="Código postal" hint="Opcional." error={errors.postalCode}>
          <Input
            value={v.postalCode ?? ""}
            onChange={(e) => {
              set("postalCode", e.target.value);
            }}
            maxLength={12}
            autoComplete="postal-code"
          />
        </Field>
      </fieldset>

      <fieldset className="grid gap-3 sm:grid-cols-2">
        <legend className="mb-1 text-xs font-medium text-fg sm:col-span-2">Contacto de facturación</legend>
        <div className="sm:col-span-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setV((prev) => ({
                ...prev,
                billingContactName: prev.contactName,
                billingContactEmail: prev.contactEmail,
                billingContactPhone: prev.contactPhone,
              }));
            }}
          >
            Usar el contacto comercial
          </Button>
        </div>
        <Field label="Nombre de facturación" error={errors.billingContactName} className="sm:col-span-2">
          <Input
            value={v.billingContactName}
            onChange={(e) => {
              set("billingContactName", e.target.value);
            }}
            maxLength={120}
            required
          />
        </Field>
        <Field label="Correo de facturación" error={errors.billingContactEmail}>
          <Input
            value={v.billingContactEmail}
            onChange={(e) => {
              set("billingContactEmail", e.target.value);
            }}
            type="email"
            maxLength={320}
            required
          />
        </Field>
        <Field label="Teléfono de facturación" error={errors.billingContactPhone}>
          <Input
            value={v.billingContactPhone}
            onChange={(e) => {
              set("billingContactPhone", e.target.value);
            }}
            type="tel"
            maxLength={24}
            required
          />
        </Field>
      </fieldset>

      {formError ? (
        <p className="text-sm text-danger" role="alert">
          {formError}
        </p>
      ) : null}
      <Button type="submit" variant="primary" size="lg" disabled={pending}>
        {pending ? "Guardando…" : submitLabel}
      </Button>
    </form>
  );
}
